import React, { useState, useEffect } from 'react'
import { Modal, Button, List, Avatar, Typography, Tag, message, Input, Form, Divider, Space, Upload, Tooltip } from 'antd'
import { UserOutlined, LogoutOutlined, CheckCircleOutlined, LockOutlined, EditOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { useAuth } from '../contexts/AuthContext'
import { courses } from '../data/courses'
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, getAuth } from 'firebase/auth'
import { doc, updateDoc, setDoc } from 'firebase/firestore'
import { db } from '../config/firebaseConfig'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage, auth } from '../config/firebaseConfig'
import { getFirebaseErrorKey, isNetworkError } from '../utils/firebaseErrorHandler'

const { Title, Text, Link } = Typography

interface UserProfileProps {
  visible: boolean
  onClose: () => void
}

const UserProfile: React.FC<UserProfileProps> = ({ visible, onClose }) => {
  const { user, userProfile, logout, hasCourseAccess, refreshUserProfile, isAnonymous } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation('user')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '')
  const [editingDisplayName, setEditingDisplayName] = useState(false)
  const [savingDisplayName, setSavingDisplayName] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  // Update displayName when userProfile changes
  React.useEffect(() => {
    setDisplayName(userProfile?.displayName || '')
  }, [userProfile?.displayName])

  const handleLogout = async () => {
    try {
      await logout()
      onClose()
      message.success(t('profile.messages.loggedOut'))
    } catch (error) {
      message.error(t('profile.messages.logoutFailed'))
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      message.error(t('profile.validation.fillAllFields'))
      return
    }

    if (newPassword !== confirmPassword) {
      message.error(t('profile.validation.passwordsDoNotMatch'))
      return
    }

    if (newPassword.length < 6) {
      message.error(t('profile.validation.passwordTooShort'))
      return
    }

    if (!user) {
      message.error(t('profile.validation.userNotAuthenticated'))
      return
    }

    if (!user.email) {
      message.error(t('profile.validation.userNotAuthenticated'))
      return
    }

    setChangingPassword(true)
    try {
      // Reauthenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)

      // Update password
      await updatePassword(user, newPassword)

      message.success(t('profile.messages.passwordChanged'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: unknown) {
      console.error('Password change error:', error)
      const firebaseError = error as { code?: string; message?: string }
      
      // Check for network errors
      if (isNetworkError(error)) {
        message.error(t('profile.messages.networkError'))
      } else if (firebaseError.code === 'auth/wrong-password') {
        message.error(t('profile.messages.wrongPassword'))
      } else if (firebaseError.code === 'auth/weak-password') {
        message.error(t('profile.messages.weakPassword'))
      } else if (firebaseError.code === 'auth/requires-recent-login') {
        message.error(t('profile.messages.requiresRecentLogin'))
      } else if (firebaseError.code === 'auth/user-disabled') {
        message.error(t('profile.messages.userDisabled'))
      } else {
        message.error(t('profile.messages.updateFailed'))
      }
    } finally {
      setChangingPassword(false)
    }
  }

  const handleSaveDisplayName = async () => {
    if (!user || !user.email) return

    setSavingDisplayName(true)
    try {
      // Use UID-based document ID like AuthContext
      const userDocId = user.uid
      const userRef = doc(db, 'users', userDocId)
      
      await setDoc(userRef, {
        displayName: displayName.trim() || null
      }, { merge: true })
      
      await refreshUserProfile()
      setEditingDisplayName(false)
      message.success(t('profile.messages.displayNameUpdated'))
    } catch (error) {
      console.error('Error updating display name:', error)
      if (isNetworkError(error)) {
        message.error(t('profile.messages.networkError'))
      } else {
        message.error(t('profile.messages.updateFailed'))
      }
    } finally {
      setSavingDisplayName(false)
    }
  }

  const handleAvatarUpload = async (file: File) => {
    if (!user || !user.email || !user.uid) {
      message.error('You must be logged in to upload an avatar')
      return
    }

    console.log('Starting avatar upload for user:', user.uid)
    console.log('Firebase auth currentUser:', auth.currentUser?.uid)
    console.log('Storage bucket from ref:', storage.app.options.storageBucket)

    // Validate file size (max 2MB for avatars)
    if (file.size > 2 * 1024 * 1024) {
      message.error(t('profile.messages.avatarFileTooLarge'))
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      message.error(t('profile.messages.avatarInvalidType'))
      return
    }

    setUploadingAvatar(true)
    try {
      const userId = user.uid // Use UID for storage path
      const timestamp = Date.now()
      const fileName = `avatar_${userId}_${timestamp}.${file.type.split('/')[1]}`
      const storagePath = `user-avatars/${fileName}`
      console.log('Uploading to path:', storagePath)
      const storageRef = ref(storage, storagePath)
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)

      // Delete old avatar if exists
      if (userProfile?.avatar) {
        try {
          // Extract path from Firebase Storage download URL
          const url = new URL(userProfile.avatar)
          const pathStart = url.pathname.indexOf('/o/') + 3
          const pathEnd = url.pathname.indexOf('?') !== -1 ? url.pathname.indexOf('?') : url.pathname.length
          const storagePath = decodeURIComponent(url.pathname.substring(pathStart, pathEnd))
          const oldAvatarRef = ref(storage, storagePath)
          await deleteObject(oldAvatarRef)
        } catch (error) {
          console.warn('Could not delete old avatar:', error)
        }
      }

      // Update user profile with new avatar URL
      const userDocId = user.uid
      const userRef = doc(db, 'users', userDocId)
      await setDoc(userRef, {
        avatar: downloadURL
      }, { merge: true })

      await refreshUserProfile()
      message.success('Avatar updated successfully')
    } catch (error) {
      console.error('Error uploading avatar:', error)
      if (isNetworkError(error)) {
        message.error(t('profile.messages.avatarUploadFailed'))
      } else {
        message.error(t('profile.messages.avatarUploadFailed'))
      }
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleRemoveAvatar = async () => {
    if (!user || !user.email) {
      message.error('User not authenticated')
      return
    }

    setUploadingAvatar(true)
    try {
      // Delete avatar from storage if exists
      if (userProfile?.avatar) {
        try {
          // Extract path from Firebase Storage download URL
          const url = new URL(userProfile.avatar)
          const pathStart = url.pathname.indexOf('/o/') + 3
          const pathEnd = url.pathname.indexOf('?') !== -1 ? url.pathname.indexOf('?') : url.pathname.length
          const storagePath = decodeURIComponent(url.pathname.substring(pathStart, pathEnd))
          const oldAvatarRef = ref(storage, storagePath)
          await deleteObject(oldAvatarRef)
        } catch (error) {
          console.warn('Could not delete avatar from storage:', error)
        }
      }

      // Update user profile to remove avatar
      const userDocId = user.uid
      const userRef = doc(db, 'users', userDocId)
      await setDoc(userRef, {
        avatar: null
      }, { merge: true })

      await refreshUserProfile()
      message.success('Avatar removed successfully')
    } catch (error) {
      console.error('Error removing avatar:', error)
      if (isNetworkError(error)) {
        message.error(t('profile.messages.avatarRemoveFailed'))
      } else {
        message.error(t('profile.messages.avatarRemoveFailed'))
      }
    } finally {
      setUploadingAvatar(false)
    }
  }

  const availableCourses = Object.values(courses).filter(course => hasCourseAccess(course.id))

  const [showAvatarActions, setShowAvatarActions] = useState(false)

  return (
    <Modal
      title={
        <Space align="center" size={12}>
          <div
            style={{ position: 'relative', cursor: 'pointer' }}
            onMouseEnter={() => setShowAvatarActions(true)}
            onMouseLeave={() => setShowAvatarActions(false)}
          >
            <Avatar 
              size={80} 
              src={userProfile?.avatar} 
              icon={<UserOutlined />} 
            />
            {showAvatarActions && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {userProfile?.avatar ? (
                  <Tooltip title="Remove avatar">
                    <DeleteOutlined 
                      style={{ color: 'white', fontSize: 24, cursor: 'pointer' }}
                      onClick={handleRemoveAvatar}
                    />
                  </Tooltip>
                ) : (
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleAvatarUpload(file)
                      return false
                    }}
                    disabled={uploadingAvatar}
                  >
                    <Tooltip title="Upload avatar">
                      <UploadOutlined style={{ color: 'white', fontSize: 24, cursor: 'pointer' }} />
                    </Tooltip>
                  </Upload>
                )}
              </div>
            )}
          </div>
          <Space direction="vertical" size={0} style={{ flex: 1 }}>
            <Title level={4} style={{ margin: 0 }}>
              {isAnonymous ? 'Guest' : user?.email}
            </Title>
            <Text type="secondary">{isAnonymous ? 'Guest User' : t('profile.title')}</Text>
          </Space>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="logout" danger icon={<LogoutOutlined />} onClick={handleLogout}>
          {t('profile.buttons.logout')}
        </Button>,
        <Button key="close" onClick={onClose}>
          {t('profile.buttons.close')}
        </Button>
      ]}
      width={600}
    >
      {!isAnonymous && (
        <>
          <Title level={5}>{t('profile.changePassword')}</Title>
          <Form layout="vertical">
            <Form.Item label={t('profile.form.currentPassword')}>
              <Input.Password
                placeholder={String(t('profile.form.placeholders.currentPassword'))}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                iconRender={(visible) => (visible ? <LockOutlined /> : <LockOutlined />)}
              />
            </Form.Item>
            <Form.Item label={t('profile.form.newPassword')}>
              <Input.Password
                placeholder={String(t('profile.form.placeholders.newPassword'))}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                iconRender={(visible) => (visible ? <LockOutlined /> : <LockOutlined />)}
              />
            </Form.Item>
            <Form.Item label={t('profile.form.confirmNewPassword')}>
              <Input.Password
                placeholder={String(t('profile.form.placeholders.confirmNewPassword'))}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onPressEnter={handleChangePassword}
                iconRender={(visible) => (visible ? <LockOutlined /> : <LockOutlined />)}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                onClick={handleChangePassword}
                loading={changingPassword}
              >
                {t('profile.buttons.changePassword')}
              </Button>
            </Form.Item>
          </Form>

          <Divider style={{ borderColor: '#bbbbbb' }} />

          <Title level={5}>{t('profile.displayName')}</Title>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            {editingDisplayName ? (
              <Space>
                <Input
                  placeholder={String(t('profile.form.placeholders.displayName'))}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                  onPressEnter={handleSaveDisplayName}
                  style={{ width: 200 }}
                />
                <Button
                  type="primary"
                  onClick={handleSaveDisplayName}
                  loading={savingDisplayName}
                  size="small"
                >
                  {t('profile.buttons.save')}
                </Button>
                <Button
                  onClick={() => {
                    setDisplayName(userProfile?.displayName || '')
                    setEditingDisplayName(false)
                  }}
                  size="small"
                >
                  {t('profile.buttons.cancel')}
                </Button>
              </Space>
            ) : (
              <Space>
                <Text>
                  {userProfile?.displayName || 'No display name set'}
                </Text>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setEditingDisplayName(true)}
                  size="small"
                >
                  {t('profile.buttons.edit')}
                </Button>
              </Space>
            )}
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This name will be shown when you leave comments on videos
            </Text>
          </Space>

          <Divider style={{ borderColor: '#bbbbbb' }} />
        </>
      )}
      <List
        dataSource={availableCourses}
        renderItem={(course) => (
          <List.Item
            actions={[
              <Tag key="accessible" color="green" icon={<CheckCircleOutlined />}>
                Accessible
              </Tag>
            ]}
          >
            <List.Item.Meta
              title={
                <Link 
                  onClick={() => {
                    navigate(`/${course.routePath}`)
                    onClose()
                  }}
                >
                  {course.id}
                </Link>
              }
              description={`/${course.routePath}`}
            />
          </List.Item>
        )}
        locale={{ emptyText: 'No courses available' }}
      />
    </Modal>
  )
}

export default UserProfile